import { Volume2, VolumeX } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/shared/components/Modal'

/** The ambient loop that plays while the explainer is open (48 s, seamless). */
const ZEN_LOOP_URL = '/assets/audio/zen_loop.m4a'
const ZEN_VOLUME = 0.35

/**
 * One-time explainer shown at app start: three short paragraphs on why
 * one-second scrolling hurts focus and mood and why staying on a post is good for you,
 * with the key phrases in the accent colour, over a soft zen loop. Close
 * unlocks only after the "I read this" slider is dragged to the end; the
 * caller records the dismissal so it never shows again. Mirrors the mobile
 * SocialSanityIntroPopup.
 */
export function SocialSanityIntroModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [progress, setProgress] = useState(0)
  const [muted, setMuted] = useState(false)
  // Browsers only allow sound after a gesture: the loop starts on open where
  // it can, otherwise on the first press inside the dialog.
  const [blocked, setBlocked] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const confirmed = progress >= 100

  useEffect(() => {
    if (!open) return
    const audio = new Audio(ZEN_LOOP_URL)
    audio.loop = true
    audio.volume = ZEN_VOLUME
    audioRef.current = audio
    audio.play().catch(() => setBlocked(true))
    return () => {
      audio.pause()
      audioRef.current = null
    }
  }, [open])

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted
  }, [muted])

  const unlockAudio = () => {
    if (!blocked || !audioRef.current) return
    audioRef.current
      .play()
      .then(() => setBlocked(false))
      .catch(() => {})
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        /* the slider + Close are the only way out */
      }}
      size="md"
    >
      <div className="flex flex-col items-center gap-4 text-center" onPointerDown={unlockAudio}>
        <div className="flex w-full items-start justify-between">
          <span className="w-8" aria-hidden />
          <span className="text-5xl" aria-hidden>
            🧘
          </span>
          <button
            type="button"
            onClick={() => setMuted((value) => !value)}
            className="text-fg-subtle hover:text-fg rounded-full p-1.5 transition"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="size-5" /> : <Volume2 className="size-5" />}
          </button>
        </div>
        <h2 className="text-fg text-xl font-bold">{t('socialSanity.introTitle')}</h2>
        <div className="text-fg-muted max-h-72 space-y-3 overflow-y-auto text-left text-sm leading-relaxed">
          <p>{highlight(t('socialSanity.introP1'))}</p>
          <p>{highlight(t('socialSanity.introP2'))}</p>
          <p>{highlight(t('socialSanity.introP3'))}</p>
        </div>

        <label className="w-full">
          <span className="text-fg-muted mb-1 block text-xs font-semibold">
            {confirmed ? t('socialSanity.introConfirmed') : t('socialSanity.introSlide')}
          </span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={progress}
            onChange={(event) => {
              if (!confirmed) setProgress(Number(event.target.value))
            }}
            className="w-full"
            style={{ accentColor: confirmed ? '#34c759' : undefined }}
            aria-label={t('socialSanity.introSlide')}
          />
        </label>

        <button
          type="button"
          onClick={onClose}
          disabled={!confirmed}
          className="bg-primary hover:bg-primary-hover w-full rounded-xl py-2.5 text-sm font-semibold text-white transition disabled:opacity-40"
        >
          {tc('actions.close')}
        </button>
      </div>
    </Modal>
  )
}

/**
 * Renders a paragraph whose `**phrases**` are painted in the accent colour
 * (the markup is shared with the mobile strings). Dark themes use the lighter
 * secondary tint: the primary is a deep tone that reads as blue on black.
 */
function highlight(text: string): ReactNode {
  const parts = text.split('**')
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="text-primary dark:text-secondary font-semibold">
        {part}
      </span>
    ) : (
      <span key={index}>{part}</span>
    ),
  )
}
